import { Phone, MapPin, Package, CreditCard } from 'lucide-react';
import { cur } from '@/lib/currency';

const PAYMENT_STATUS = {
  paid:       { label: 'مدفوع',        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  partial:    { label: 'جزئي',         cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  overdue:    { label: 'متأخر',        cls: 'bg-red-50 text-red-700 border-red-200' },
  not_paid:   { label: 'غير مدفوع',   cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  to_invoice: { label: 'للفوترة',      cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  no_invoice: { label: 'بدون فاتورة', cls: 'bg-muted text-muted-foreground border-border' },
};

function fmt(n, decimals = 2) {
  return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export default function OrderHeroCard({ order }) {
  const partner = order.partner_details;
  const pStatus = PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.no_invoice;
  const lines = order.lines || [];
  const totalQty = lines.reduce((s, l) => s + (l.product_uom_qty || 0), 0);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
      {/* Top gradient strip */}
      <div className="bg-gradient-to-l from-primary/10 to-primary/5 px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-2xl font-black text-foreground tracking-tight">{order.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{formatDate(order.date_order)}</p>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${pStatus.cls} flex items-center gap-1 whitespace-nowrap`}>
            <CreditCard className="w-3 h-3" /> {pStatus.label}
          </span>
        </div>
      </div>

      {/* Customer row */}
      <div className="px-4 py-3 flex items-center gap-3 border-b border-border">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {partner?.image_128
            ? <img src={`data:image/jpeg;base64,${partner.image_128}`} alt="" className="w-full h-full object-cover" />
            : <span className="text-primary font-black text-lg">{(partner?.name || order.partner_id?.[1] || '?').charAt(0)}</span>
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-foreground truncate">
            {partner?.name || (Array.isArray(order.partner_id) ? order.partner_id[1] : '—')}
          </p>
          {(partner?.phone || partner?.mobile) && (
            <div className="flex items-center gap-1 mt-0.5">
              <Phone className="w-3 h-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">{partner.phone || partner.mobile}</p>
            </div>
          )}
          {(partner?.city || partner?.street) && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground truncate">{[partner.street, partner.city].filter(Boolean).join('، ')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 divide-x divide-x-reverse divide-border">
        <div className="px-3 py-3 text-center">
          <p className="text-xl font-black text-primary">{fmt(order.amount_total)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">الإجمالي {cur()}</p>
        </div>
        <div className="px-3 py-3 text-center">
          <p className="text-xl font-black text-foreground">{lines.length}</p>
          <p className="text-xs text-muted-foreground mt-0.5">منتج</p>
        </div>
        <div className="px-3 py-3 text-center">
          <p className="text-xl font-black text-foreground">{fmt(totalQty, 0)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">وحدة</p>
        </div>
      </div>

      {/* Payment progress bar */}
      {order.amount_total > 0 && (order.payment_status === 'paid' || order.payment_status === 'partial') && (
        <div className="px-4 pb-3 space-y-1.5">
          {/* `amount_due` isn't returned by the order endpoint, so it read as 0.
              Derive the remaining from total - paid instead. */}
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">مدفوع: <span className="font-semibold text-emerald-600">{fmt(order.amount_paid)} {cur()}</span></span>
            <span className="text-muted-foreground">متبقي: <span className="font-semibold text-amber-600">{fmt(Math.max(0, (order.amount_total || 0) - (order.amount_paid || 0)))} {cur()}</span></span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${Math.min(100, ((order.amount_paid || 0) / order.amount_total) * 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}